/**
 * Type definitions for Agents.
 * These interfaces, aliases, or model helpers describe the shared data contract used
 * by the surrounding feature or UI layer.
 */
import { AddressDetails } from '../../shared/models/address.types';
import { ContactDetails } from '../../shared/models/contact.types';

export type AgentType = 'Employee' | 'Contract worker' | 'Intern';
export type AgentStatus = 'active' | 'inactive';
export type AgentApprovalStatus = 'pending' | 'approved' | 'rejected';
export type TaskPriority = 'high' | 'medium' | 'low';
export type TaskStatus = 'open' | 'in-progress' | 'completed' | 'cancelled';
export type AgentTargetType = 'stock' | 'float-sale' | 'recharge-pos' | 'customer-onboard';
export type AgentTargetPeriod = 'daily' | 'weekly' | 'monthly';
export type AgentTargetProgressStatus = 'not-started' | 'in-progress' | 'met' | 'pending-source';

export interface GuarantorContact {
  name: string;
  phoneNumber: string;
}

export interface AgentProfile {
  agentType: AgentType;
  dealerId?: string;
  joinedAt?: string;
  guarantorContact1: GuarantorContact;
  guarantorContact2: GuarantorContact;
}

export interface AgentAgencyAssignment {
  id: string;
  name: string;
  city?: string;
  area?: string;
}

export interface PosAssignment {
  id: string;
  name: string;
  posNumber?: string;
  phone?: string;
  city?: string;
  area?: string;
  agencyId?: string;
  agencyName?: string;
}

export interface AgentTaskSummary {
  total: number;
  open: number;
  inProgress: number;
  completed: number;
}

export interface AgentModel {
  id: string;
  authUserId: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  contact: ContactDetails;
  phoneDisplay: string;
  address: AddressDetails;
  city: string;
  area: string;
  assignedAgencies: AgentAgencyAssignment[];
  assignedPos: PosAssignment[];
  agencyId: string;
  agencyName: string;
  posAssigned: number;
  agentType: AgentType;
  agentProfile: AgentProfile;
  status: AgentStatus;
  approvalStatus?: string;
  taskSummary?: AgentTaskSummary;
  dealerId: string;
  joinedAt: string;
}

export interface AgentApprovalRequestModel {
  id: string;
  name: string;
  authUserId: string;
  email: string;
  contact: ContactDetails;
  address: AddressDetails;
  requestedAgency: AgentAgencyAssignment;
  requestedPos: PosAssignment[];
  requestDate: string;
  approvalStatus: AgentApprovalStatus;
  agentType: AgentType;
  agentProfile: AgentProfile;
  dealerId: string;
}

export interface AgentUpsertPayload {
  name: string;
  status: AgentStatus;
  dealerId: string;
  joinedAt: string;
  contact: ContactDetails;
  address: AddressDetails;
  assignedAgencies: AgentAgencyAssignment[];
  assignedPos: PosAssignment[];
  profile: {
    agent: AgentProfile;
  };
}

export interface TaskActivityResource {
  category: { id?: string; name: string };
  products: {
    product: { id?: string; name: string };
    quantity: number;
    unit?: string;
    productKind?: string;
    sellingPrice?: number;
    variants?: {
      variant: { id: string; name: string };
      quantity: number;
      unit?: string;
      productKind?: string;
      sellingPrice?: number;
      productReference?: string;
    }[];
  }[];
}

export interface TaskActivity {
  id: string;
  activityType: string;
  note?: string;
  posId?: string;
  posName?: string;
  checkIn?: { point?: unknown; capturedAt?: string; addressLabel?: string };
  checkOut?: { point?: unknown; capturedAt?: string; addressLabel?: string };
  distributedResources: TaskActivityResource[];
  collectedResources: TaskActivityResource[];
  returnedResources: TaskActivityResource[];
  evidence?: unknown[];
  createdAt?: string;
  createdByAuthUserId?: string;
  createdByName?: string;
  taskId?: string;
  taskTitle?: string;
  taskDescription?: string;
  taskDueDate?: string;
  taskStatus?: string;
  taskApprovalStatus?: string;
  taskPriority?: string;
  assigneeAuthUserId?: string;
  assigneeUserType?: string;
  assignedAgencyId?: string;
  assignedAgencyName?: string;
}

export interface TaskApproval {
  status: string;
  submittedAt?: string;
  submittedBy?: { id: string; name: string };
  reviewedAt?: string;
  reviewedBy?: { id: string; name: string };
  reviewerNotes?: string;
  rejectionReason?: string;
}

export interface TaskStockToCarry {
  category: { id?: string; name: string };
  products: {
    product: { id?: string; name: string };
    quantity?: number;
    unit?: string;
    productKind?: string;
    sellingPrice?: number;
    variants?: {
      variant: { id?: string; name: string };
      quantity: number;
      unit?: string;
      productKind?: string;
      sellingPrice?: number;
    }[];
  }[];
}

export interface TaskModel {
  id: string;
  title: string;
  description: string;
  instructions?: string;
  assigneeAuthUserId: string;
  assigneeUserType: string;
  assignedByAuthUserId?: string;
  assignedByName?: string;
  assignedAgencyId?: string;
  assignedAgencyName?: string;
  assignedPosIds?: string[];
  linkedPosDetails?: { id: string; name: string }[];
  dueDate: string;
  priority: TaskPriority;
  status: string;
  approval?: TaskApproval;
  stockToCarry?: TaskStockToCarry[];
  cashToCarry?: number;
  progressPercent?: number;
  activityCount?: number;
  latestActivityAt?: string;
  activities?: TaskActivity[];
  createdDate?: string;
  updatedDate?: string;
  assetAssignments?: TaskAssetAssignment[];
}

export interface TaskAssetAssignment {
  assetId: string;
  assetName: string;
  quantity: number;
  sourceAgencyId: string;
  sourceAgencyName: string;
  remarks?: string;
  categoryId?: string;
  categoryName?: string;
  productId?: string;
  productName?: string;
  variantId?: string;
  variantName?: string;
  sellingPrice?: number;
}

export interface AgentAssignedAssetRecord {
  id: string;
  agentId: string;
  assignedAt: string;
  assetId: string;
  assetName: string;
  quantity: number;
  sourceAgencyId: string;
  sourceAgencyName: string;
  assignedBy: string;
  relatedTaskId?: string;
  relatedTaskTitle?: string;
  status: 'assigned' | 'partially-used' | 'returned';
  remarks?: string;
}

export interface AgentRecord extends AgentModel {}

export interface AgentApprovalRequestRecord extends AgentApprovalRequestModel {}

export interface TaskRecord extends TaskModel {}

export interface AgentTargetProgress {
  achieved: number;
  target: number;
  percent: number;
  status: AgentTargetProgressStatus;
}

export interface AgentTargetSummaryRecord {
  id?: string;
  targetType: AgentTargetType;
  categoryId: string | null;
  categoryName?: string;
  productId: string | null;
  productName?: string;
  dailyTarget: number;
  weeklyTarget: number;
  monthlyTarget: number;
}

export interface AgentTargetRecord {
  id: string;
  targetUserId: string;
  targetUserName: string;
  userBucket: 'AGENT';
  agencyId: string;
  agencyName: string;
  type: AgentTargetType;
  period: AgentTargetPeriod;
  targetValue: number;
  dailyTarget?: number;
  weeklyTarget?: number;
  monthlyTarget?: number;
  categoryId?: string;
  categoryName?: string;
  productId?: string;
  productName?: string;
  targetSummary?: AgentTargetSummaryRecord[];
  notes?: string;
  startDate?: string;
  endDate?: string;
  progress: AgentTargetProgress;
  status: AgentTargetProgressStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface AgentTargetAnalyticsMetrics {
  todayAchievement: number;
  previousDay: number;
  dodProgress: number | null;
  actualWeek: number;
  previousWeek: number;
  wowProgress: number | null;
  mtdActual: number;
  mtdPrevious: number;
  momProgress: number | null;
  mtdTarget: number;
  actualVsMtdTarget: number | null;
  gapMtdActualVsMtdTarget: number;
}

export interface AgentTargetAnalyticsRow extends AgentTargetAnalyticsMetrics {
  id: string;
  userType: 'AGENT' | 'BTL' | string;
  userId: string;
  agencyId: string;
  targetType: AgentTargetType;
  categoryId: string | null;
  productId: string | null;
  sourceStatus: AgentTargetProgressStatus | string;
  dailyTarget: number;
  weeklyTarget: number;
  monthlyTarget: number;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  userName: string;
  agencyName: string;
  categoryName?: string;
  productName?: string;
}

export interface AgentTargetAnalyticsSection {
  targetType: AgentTargetType;
  title: string;
  sourceStatus: AgentTargetProgressStatus | string;
  rows: AgentTargetAnalyticsRow[];
  total: AgentTargetAnalyticsMetrics;
}

export interface AgentTargetAnalyticsResult {
  sections: AgentTargetAnalyticsSection[];
  page: number;
  limit: number;
  total: number;
}

export const DEFAULT_GUARANTOR_CONTACT: GuarantorContact = {
  name: '',
  phoneNumber: ''
};

export const DEFAULT_AGENT_PROFILE: AgentProfile = {
  agentType: 'Employee',
  guarantorContact1: { ...DEFAULT_GUARANTOR_CONTACT },
  guarantorContact2: { ...DEFAULT_GUARANTOR_CONTACT }
};

/**
 * BUSINESS LOGIC: toAgentRecord
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
export function toAgentRecord(id: string, payload: AgentUpsertPayload, current?: AgentRecord): AgentRecord {
  const primaryAgency = payload.assignedAgencies[0];
  const authUserId = current?.authUserId || payload.contact.email || id;

  return {
    id,
    authUserId,
    name: payload.name,
    email: payload.contact.email,
    contact: payload.contact,
    phoneDisplay: formatAgentPhoneDisplay(payload.contact),
    address: payload.address,
    city: payload.address.locality,
    area: payload.address.area,
    assignedAgencies: payload.assignedAgencies,
    assignedPos: payload.assignedPos,
    agencyId: primaryAgency?.id ?? '',
    agencyName: primaryAgency?.name ?? 'Unassigned',
    posAssigned: payload.assignedPos.length,
    agentType: payload.profile.agent.agentType,
    agentProfile: {
      agentType: payload.profile.agent.agentType,
      guarantorContact1: {
        ...DEFAULT_GUARANTOR_CONTACT,
        ...(payload.profile.agent.guarantorContact1 ?? {})
      },
      guarantorContact2: {
        ...DEFAULT_GUARANTOR_CONTACT,
        ...(payload.profile.agent.guarantorContact2 ?? {})
      }
    },
    status: payload.status,
    dealerId: payload.dealerId,
    joinedAt: payload.joinedAt,
    ...(current ? { id: current.id } : {})
  };
}

/**
 * BUSINESS LOGIC: formatAgentPhoneDisplay
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
export function formatAgentPhoneDisplay(contact: ContactDetails): string {
  const dialCode = contact.phone.dialCode ? `+${contact.phone.dialCode}` : '';
  return [dialCode, contact.phone.number].filter(Boolean).join(' ').trim();
}
